<template lang="pug">
div.app-shell
    nav(v-if="user").navbar.navbar-light.bg-light.fixed-top.app-navbar
        ul.nav.nav-pills
            li.nav-item(v-if="isAdmin")
                router-link.nav-link(to="/admin", :class="{active: adminSectionActive}") Admin
            li.nav-item(v-if="hasBiobank"): router-link.nav-link(to="/biobank") Biobank
            li.nav-item(v-if="hasBiobank"): router-link.nav-link(to="/collections") Collections
            li.nav-item(v-if="hasBiobank"): router-link.nav-link(to="/people") People
        span.text-muted.mr-3(v-if="biobank") {{displayBiobank}}
        .nav-item
            router-link.mr-sm-3(to="/user") {{user}}
            button.btn.btn-danger(@click="logout") Sign out
    main.app-main(:class="{'with-navbar': user}")
        .container.mt-sm-3: router-view
</template>

<script>
import {logout, getSession, getBiobank} from "./api";

export default {
    data() {
        return {
            user: localStorage.getItem("user"),
            biobank: localStorage.getItem("biobank"),
            biobankName: localStorage.getItem("biobank_name") || "",
            isAdmin: localStorage.getItem("is_admin") === "1",
            sessionRefreshTimer: null
        }
    },
    computed: {
        hasBiobank() {
            return !!this.biobank;
        },
        adminSectionActive() {
            const path = (this.$route && this.$route.path) || "";
            return path.startsWith("/admin")
                || path === "/networks"
                || path.startsWith("/network/");
        },
        displayBiobank() {
            return this.biobankName || this.biobank;
        }
    },
    methods: {
        isOidcLoginInProgress() {
            if (typeof sessionStorage === "undefined") return false;
            return sessionStorage.getItem("oidc_login_in_progress") === "1";
        },
        hasRecentOidcLoginAttempt() {
            const raw = localStorage.getItem("oidc_login_started_at_ms");
            const value = Number(raw);
            if (!Number.isFinite(value) || value <= 0) return false;
            return (Date.now() - value) < 120000;
        },
        useHashRouting() {
            const runtimeConfig = (typeof window !== "undefined" && window.__APP_CONFIG__)
                ? window.__APP_CONFIG__
                : null;
            return !!(runtimeConfig && runtimeConfig.routerMode === "hash");
        },
        getLoginUrl() {
            if (typeof window === "undefined") return "/login";
            if (!this.useHashRouting()) return "/login";
            return `${window.location.pathname}${window.location.search}#/login`;
        },
        isLoginLocation() {
            if (typeof window === "undefined") return false;
            if (this.useHashRouting()) {
                return (window.location.hash || "").startsWith("#/login");
            }
            return window.location.pathname === "/login";
        },
        loadBiobankName() {
            if (!this.biobank) {
                this.biobankName = "";
                localStorage.removeItem("biobank_name");
                return;
            }
            getBiobank(this.biobank)
            .then(biobank => {
                const name = (biobank && biobank.name) ? biobank.name : "";
                this.biobankName = name;
                if (name) localStorage.setItem("biobank_name", name);
                else localStorage.removeItem("biobank_name");
            })
            .catch(() => {
                this.biobankName = "";
                localStorage.removeItem("biobank_name");
            });
        },
        updateLocalSession(session) {
            localStorage.setItem("user", session.email);
            localStorage.setItem("is_admin", session.is_admin ? "1" : "0");
            localStorage.setItem("allowed_domains", JSON.stringify(session.allowed_domains || []));
            if (session.biobank) localStorage.setItem("biobank", session.biobank);
            else localStorage.removeItem("biobank");

            this.user = session.email;
            this.biobank = session.biobank || "";
            this.isAdmin = !!session.is_admin;
            this.loadBiobankName();
        },
        handleForcedLogout() {
            this.user = "";
            this.biobank = "";
            this.biobankName = "";
            this.isAdmin = false;
            if (typeof window !== "undefined" && !this.isLoginLocation()) {
                window.location.replace(this.getLoginUrl());
            }
        },
        refreshSession() {
            getSession()
            .then(session => {
                this.updateLocalSession(session);
                const path = (this.$route && this.$route.path) || "";
                if (!session || !session.email) return;
                const target = session.is_admin
                    ? "/admin/access"
                    : (session.biobank ? "/biobank" : "/user");
                const hasPostLoginRedirectPending = localStorage.getItem("post_login_redirect_pending") === "1";

                if (hasPostLoginRedirectPending) {
                    localStorage.removeItem("post_login_redirect_pending");
                    if (path !== target) {
                        this.$router.replace(target).catch(() => {});
                    }
                    return;
                }

                if (path === "/") {
                    this.$router.replace(target).catch(() => {});
                    return;
                }

                if (
                    path === "/login" ||
                    path === "/auth/oidc/callback" ||
                    (path === "/user" && (this.isOidcLoginInProgress() || this.hasRecentOidcLoginAttempt()))
                ) {
                    this.$router.replace("/").catch(() => {});
                }
            })
            .catch(err => {
                if (err && (err.status === 403 || err.name === "AbortError")) {
                    const path = (this.$route && this.$route.path) || "";
                    const isOidcFlowPath = path === "/auth/oidc/callback" || path === "/login";
                    if (isOidcFlowPath && (this.isOidcLoginInProgress() || this.hasRecentOidcLoginAttempt())) return;
                    this.handleForcedLogout();
                }
            });
        },
        startSessionRefreshTimer() {
            if (this.sessionRefreshTimer) clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = setInterval(() => {
                this.refreshSession();
            }, 5000);
        },
        logout() {
            sessionStorage.setItem("recent_logout", "1");
            const lastLoginMethod = localStorage.getItem("last_login_method");
            localStorage.clear();
            if (lastLoginMethod === "local" || lastLoginMethod === "oidc") {
                localStorage.setItem("last_login_method", lastLoginMethod);
            }
            document.cookie = "connect.sid=;";
            document.cookie = "connect.sid2=;";
            document.cookie = "bbmri_node_sid=;path=/api;";
            document.cookie = "bbmri_node_sid=;";
            logout()
            .then(() => {
                this.user = "";
                this.biobank = "";
                this.biobankName = "";
                this.isAdmin = false;
                this.$router.push("/login");
            });
        }
    },
    watch: {
        $route() {
            this.user = localStorage.getItem("user");
            this.biobank = localStorage.getItem("biobank");
            this.biobankName = localStorage.getItem("biobank_name") || "";
            this.isAdmin = localStorage.getItem("is_admin") === "1";
            this.loadBiobankName();
        }
    },
    created() {
        window.addEventListener("auth:logout", this.handleForcedLogout);
        this.refreshSession();
        this.startSessionRefreshTimer();
    },
    beforeDestroy() {
        window.removeEventListener("auth:logout", this.handleForcedLogout);
        if (this.sessionRefreshTimer) {
            clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = null;
        }
    }
}
</script>

<style>
.app-shell {
    height: 100vh;
    overflow: hidden;
}

.app-navbar {
    z-index: 1030;
}

.app-main {
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
}

.app-main.with-navbar {
    margin-top: 72px;
    height: calc(100vh - 72px);
}

.app-main:has(.admin-subnav-fixed) {
    height: calc(100vh - 56px);
}

.app-main.with-navbar:has(.admin-subnav-fixed) {
    height: calc(100vh - 72px - 56px);
}

.app-main:has(.page-action-footer) {
    height: calc(100vh - 68px);
}

.app-main.with-navbar:has(.page-action-footer) {
    height: calc(100vh - 72px - 68px);
}

.app-main:has(.admin-subnav-fixed):has(.page-action-footer) {
    height: calc(100vh - 56px - 68px);
}

.app-main.with-navbar:has(.admin-subnav-fixed):has(.page-action-footer) {
    height: calc(100vh - 72px - 56px - 68px);
}

.has-fixed-footer {
    padding-bottom: 84px;
}

.page-action-footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1020;
    background: #fff;
    border-top: 1px solid #dee2e6;
}

.page-action-footer .container {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}

.page-action-footer .page-action-footer-inner {
    padding: 0.75rem 1rem;
}
</style>
